import {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Button, Table, Tag} from 'antd';
import {makeSelectTabOperations} from 'redux/store/auth';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {NavTabsEnum} from 'utils/constants';
import SubscriptionPlanModal from '../components/SubscriptionPlanModal';
import SubscriptionResourceStatusModal from '../components/SubscriptionResourceStatusModal';
import {
  CHANGE_SUBSCRIPTION_PLAN_STATUS,
  GET_ALL_SUBSCRIPTION_PLANS,
} from '../resolvers';
import styles from '../subscriptionSettings.module.scss';

const SubscriptionPlansTab = () => {
  const navigate = useNavigate();
  const [modalState, setModalState] = useState({
    open: false,
    planData: null,
  });
  const [statusModalState, setStatusModalState] = useState({
    open: false,
    planData: null,
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Subscription),
  );

  const {data, loading, refetch} = useQuery(GET_ALL_SUBSCRIPTION_PLANS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
  });

  const response = data?.getAllSubscriptionPlans;
  const plans =
    response?.__typename === 'ResponseWithDabaSubscriptionPlans'
      ? response?.data || []
      : [];

  useEffect(() => {
    if (response?.__typename === 'Error') {
      toast.error(response.message);
    }
  }, [response]);

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: value => value || '-',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: value => value || '-',
      },
      {
        title: 'Visible To Users',
        dataIndex: 'isVisibleToUsers',
        key: 'isVisibleToUsers',
        render: value => (
          <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'isEnabled',
        key: 'isEnabled',
        render: value => (
          <Tag color={value ? 'green' : 'red'}>
            {value ? 'Enabled' : 'Disabled'}
          </Tag>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <div className={styles['table-actions']}>
            <Button
              type="link"
              disabled={!record?.key}
              onClick={() =>
                navigate(
                  `/dashboard/plans/subscription-setting/plans/${record?.key}`,
                )
              }>
              Subscriptions
            </Button>
            <Button
              type="link"
              disabled={!tabOperations?.update}
              onClick={() =>
                setModalState({
                  open: true,
                  planData: record,
                })
              }>
              Edit
            </Button>
            <Button
              type="link"
              disabled={!tabOperations?.update || !record?.key}
              onClick={() =>
                navigate(
                  `/dashboard/plans/subscription-setting/plans/${record?.key}/features`,
                )
              }>
              Features & Reminders
            </Button>
            <Button
              type="link"
              disabled={!tabOperations?.update || !record?.key}
              onClick={() =>
                setStatusModalState({
                  open: true,
                  planData: record,
                })
              }>
              Change Status
            </Button>
          </div>
        ),
      },
    ],
    [navigate, tabOperations?.update],
  );

  return (
    <div className={styles.content}>
      <div className={styles['plans-header']}>
        <h2>Subscription Plans</h2>
        <Button
          type="primary"
          disabled={!tabOperations?.create}
          onClick={() => setModalState({open: true, planData: null})}>
          Create Plan
        </Button>
      </div>
      <Table
        className="antd-table"
        rowKey={record => record?.key || record?.name}
        columns={columns}
        loading={loading}
        dataSource={plans}
        pagination={false}
      />
      <SubscriptionPlanModal
        isOpen={modalState.open}
        onClose={() => setModalState({open: false, planData: null})}
        planData={modalState.planData}
        refetch={refetch}
        canSubmit={
          modalState.planData ? tabOperations?.update : tabOperations?.create
        }
      />
      <SubscriptionResourceStatusModal
        isOpen={statusModalState.open}
        onClose={() => setStatusModalState({open: false, planData: null})}
        resourceData={statusModalState.planData}
        refetch={refetch}
        canSubmit={tabOperations?.update}
        mutation={CHANGE_SUBSCRIPTION_PLAN_STATUS}
        mutationName="changeSubscriptionPlanStatus"
        successTypename="ResponseWithSubscriptionPlan"
        resourceLabel="Plan"
      />
    </div>
  );
};

export default SubscriptionPlansTab;
